import {makeAutoObservable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {DataObjectId} from 'yougile-model'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {createUseStore} from '@containers/hooks'
import {getBoards} from './get-boards'
import {getColumns} from './get-columns'
import {getProjects} from './get-projects'
import {CbProps, DestinationRet} from './types'

interface State {
  project?: CbProps
  board?: CbProps
  column?: CbProps
}

export class DestinationStore {
  state: State = {}

  onChange: (props: DestinationRet) => void

  constructor(onChange: (props: DestinationRet) => void) {
    makeAutoObservable(this, {}, {autoBind: true})
    this.onChange = onChange
  }

  sync = (): void => {
    this.onChange?.({
      project: this.state.project,
      board: this.state.board,
      column: this.state.column
    })
  }

  setInitValues = (project: CbProps, board: CbProps, column: CbProps): void => {
    this.state.project = project
    this.state.board = board
    this.state.column = column
  }

  getProjectOptions = (allOption = false): OptionsItem[] => {
    const projects = getProjects()
    const lang = Lang.msg.movementTemplates

    const options: OptionsItem[] = projects.map(({id, title}) => ({
      key: id,
      text: title,
      selected: this.state.project?.id === id,

      cb: () => {
        runInAction(() => {
          this.state.project = {id, title}
          if (allOption) {
            this.state.board = {
              id: 'all' as DataObjectId,
              title: lang.allBoards
            }
            this.state.column = {
              id: 'all' as DataObjectId,
              title: lang.allColumns
            }
          } else {
            this.state.board = undefined
            this.state.column = undefined
          }

          this.sync()
        })
      }
    }))

    if (allOption) {
      options.unshift({
        key: 'all',
        text: lang.allProjects,
        selected: this.state.project?.id === 'all',
        cb: () => {
          runInAction(() => {
            this.state.project = {
              id: 'all' as DataObjectId,
              title: lang.allProjects // 'Все проекты'
            }
            this.state.board = {
              id: 'all' as DataObjectId,
              title: lang.allBoards
            }
            this.state.column = {
              id: 'all' as DataObjectId,
              title: lang.allColumns
            }
            this.sync()
          })
        }
      })
    }

    return options
  }

  getBoardOptions = (allOption = false): OptionsItem[] => {
    const boards = getBoards(this.state.project?.id)
    const lang = Lang.msg.movementTemplates

    const options: OptionsItem[] = boards.map(({id, title}) => ({
      key: id,
      text: title,
      selected: this.state.board?.id === id,

      cb: () => {
        runInAction(() => {
          this.state.board = {id, title}

          if (allOption) {
            this.state.column = {
              id: 'all' as DataObjectId,
              title: lang.allColumns
            }
          } else {
            this.state.column = undefined
          }
          this.sync()
        })
      }
    }))

    if (allOption) {
      options.unshift({
        key: 'all',
        text: lang.allBoards,
        selected: this.state.board?.id === 'all',
        cb: () => {
          runInAction(() => {
            this.state.board = {
              id: 'all' as DataObjectId,
              title: lang.allBoards
            }
            this.state.column = {
              id: 'all' as DataObjectId,
              title: lang.allColumns
            }
            this.sync()
          })
        }
      })
    }

    return options
  }

  getColumnsOptions = (
    excludeColumns: DataObjectId[],
    allOption = false
  ): OptionsItem[] => {
    const lang = Lang.msg.movementTemplates
    let columns = getColumns(this.state.board?.id)
    columns = columns.filter(c => !excludeColumns.includes(c.id))

    const options: OptionsItem[] = columns.map(({id, title}) => ({
      key: id,
      text: title,
      selected: this.state.column?.id === id,

      cb: () => {
        runInAction(() => {
          this.state.column = {id, title}
          this.sync()
        })
      }
    }))

    if (allOption) {
      options.unshift({
        key: 'all',
        text: lang.allColumns,
        selected: this.state.column?.id === 'all',
        cb: () => {
          runInAction(() => {
            this.state.column = {
              id: 'all' as DataObjectId,
              title: lang.allColumns
            }
            this.sync()
          })
        }
      })
    }

    return options
  }
}

const [StoreContext, useStore] = createUseStore(DestinationStore)
export {StoreContext, useStore}
